/// <amd-module name="Artifacts/Connect/components/GetToolPanel" />
import { CopyableCode } from "Artifacts/Connect/Components/Common";
import { OperatingSystem } from "Artifacts/Connect/Constants";
import * as Res from "Artifacts/Connect/Resources";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { getIsHosted } from "Artifacts/Root/helpers/CollectionHelpers";
import { getIsUserMember } from "Artifacts/Root/helpers/UserHelpers";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { Panel } from "VSSUI/Panel";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import * as React from "react";
import { useMemo } from "react";

interface IGetToolPanelProps {
  pageContext: IVssPageContext;
  tool: ITool;
  onDismiss: () => void;
}

interface IToolComponentProps {
  userIsMember?: boolean;
  isHosted?: boolean;
}

const GetToolComponents: {
  [toolId: number]: React.ComponentType<IToolComponentProps>;
} = {};

function getGetPackagingToolComponent(
  tool: ITool,
  userIsMember: boolean,
  isHosted: boolean,
): JSX.Element | null {
  const GetToolDetails = GetToolComponents[tool.id];
  return GetToolDetails ? <GetToolDetails userIsMember={userIsMember} isHosted={isHosted} /> : null;
}

export const GetToolPanel: React.FC<IGetToolPanelProps> = ({ pageContext, tool, onDismiss }) => {
  const { userIsMember, isHosted } = useMemo(() => {
    return {
      userIsMember: getIsUserMember(pageContext),
      isHosted: getIsHosted(pageContext),
    };
  }, [pageContext]);

  return (
    tool && (
      <Panel
        onDismiss={onDismiss}
        titleProps={{ text: Res.GetTools_Text }}
        className="get-packaging-tools body-m"
        footerButtonProps={[{ text: Res.ClosePanel, onClick: onDismiss, primary: true }]}
      >
        <div className="rhythm-vertical-20">
          {getGetPackagingToolComponent(tool, userIsMember, isHosted)}
        </div>
      </Panel>
    )
  );
};

GetToolComponents[ToolId.DotNet] = ({ userIsMember, isHosted }) => {
  if (userIsMember) {
    return (
      <React.Fragment>
        <div>
          {isHosted && Res.GetTool_DotNet_Preamble}
          {!isHosted && Res.GetTool_DotNet_OnPremPreamble}
        </div>

        <div>
          <h2 className="title-xs">{Res.Step} 1</h2>
          <p>
            <FWLink linkId="2103972">{Res.InstallDotNetCore} (2.1.400+)</FWLink>
          </p>
        </div>

        {isHosted && (
          <div>
            <h2 className="title-xs">{Res.Step} 2</h2>
            <p>
              <FWLink linkId="2099625">{Res.InstallCredProvider}</FWLink>
            </p>
          </div>
        )}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div>
          {Res.GetTool_DotNet_Preamble}
          <p>
            <FWLink linkId="2103972">{Res.InstallDotNetCore}</FWLink>
          </p>
        </div>
      </React.Fragment>
    );
  }
};

GetToolComponents[ToolId.NuGet] = ({ userIsMember, isHosted }) => {
  if (userIsMember) {
    return (
      <React.Fragment>
        <div>
          {isHosted ? (
            <React.Fragment>
              {Res.GetTool_NuGet_Preamble}
              <p>{Res.GetTool_NuGet_VisualStudioInstalled}</p>
              <p>
                <FormatComponent format={Res.GetTool_NuGet_PreambleNote}>
                  <FWLink underline linkId="2104208">
                    {Res.OnlineDocumentation}
                  </FWLink>
                </FormatComponent>
              </p>
            </React.Fragment>
          ) : (
            Res.GetTool_NuGet_OnPremPreamble
          )}
        </div>

        <div>
          <div className="title-xs">{Res.Step} 1</div>
          <p>
            <FWLink linkId="2099732">{Res.DownloadNuGet}</FWLink>
          </p>
        </div>

        {isHosted && (
          <div>
            <h2 className="title-xs">{Res.Step} 2</h2>
            <p>{Res.InstallCredProvider_Preamble}</p>
            <p>
              <FWLink linkId="2099625">{Res.InstallCredProvider}</FWLink>
            </p>
          </div>
        )}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div>
          {Res.GetTool_NuGet_VersionPrerequisite}
          <p>{Res.GetTool_NuGet_VisualStudioInstalled}</p>
          <p>
            <FormatComponent format={Res.GetTool_NuGet_PreambleNote}>
              <FWLink linkId="2104208">{Res.OnlineDocumentation}</FWLink>
            </FormatComponent>
          </p>
          <p>
            <FWLink linkId="2099732">{Res.DownloadNuGet}</FWLink>
          </p>
        </div>
      </React.Fragment>
    );
  }
};

GetToolComponents[ToolId.VisualStudio] = () => (
  <React.Fragment>
    <div>
      {Res.GetTool_VisualStudio_Preamble}
      <p>
        <FWLink linkId="2103696">{Res.DownloadVisualStudio}</FWLink>
      </p>
    </div>
  </React.Fragment>
);

GetToolComponents[ToolId.npm] = ({ userIsMember, isHosted }) => {
  const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);
  if (userIsMember) {
    return (
      <React.Fragment>
        <TabBar
          onSelectedTabChanged={setSelectedTabId}
          selectedTabId={selectedTabId}
          tabSize={TabSize.Compact}
        >
          <Tab
            name="Windows"
            id={OperatingSystem.Windows}
            iconProps={{ iconName: "WindowsLogo" }}
          />
          <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
        </TabBar>
        {selectedTabId === OperatingSystem.Windows ? (
          <React.Fragment>
            <div>{isHosted ? Res.GetTool_Npm_Preamble : Res.GetTool_Npm_OnPremPreamble}</div>

            <div>
              <h2 className="title-xs">{Res.Step} 1</h2>
              <p>
                <FWLink linkId="2103882">{Res.DownloadNodeNpm}</FWLink>
              </p>
            </div>

            {isHosted && (
              <React.Fragment>
                <div>
                  <h2 className="title-xs">{Res.Step} 2</h2>
                  <p>{Res.RunCommand}</p>
                  <CopyableCode>
                    npm install -g vsts-npm-auth --registry https://registry.npmjs.com --always-auth
                    false
                  </CopyableCode>
                </div>
                <div className="body-s">
                  {Res.CantUseCredHelper} <br />
                  <FWLink linkId="2103971">{Res.HowToUsePAT}</FWLink>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div>{isHosted ? Res.GetTool_Npm_Preamble : Res.GetTool_Npm_OnPremPreamble}</div>

            <div>
              <h2 className="title-xs">{Res.Step} 1</h2>
              <p>
                <FWLink linkId="2103882">{Res.DownloadNodeNpm}</FWLink>
              </p>
            </div>

            <div>
              <h2 className="title-xs">{Res.Step} 2</h2>
              <p>
                <FWLink linkId="2103971">{Res.HowToUsePAT}</FWLink>
              </p>
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div>
          {Res.GetTool_NpmOnly_Preamble}
          <p>
            <FWLink linkId="2103882">{Res.DownloadNodeNpm}</FWLink>
          </p>
        </div>
      </React.Fragment>
    );
  }
};

GetToolComponents[ToolId.Maven] = () => (
  <React.Fragment>
    <div>{Res.GetTool_Maven_Preamble}</div>

    <div>
      <h2 className="title-xs">{Res.Step} 1</h2>
      <p>
        <FWLink linkId="2103970">{Res.DownloadMaven}</FWLink>
      </p>
    </div>

    <div>
      <h2 className="title-xs">{Res.Step} 2</h2>
      <p>
        <FWLink linkId="2103881">{Res.InstallMaven}</FWLink>
      </p>
    </div>
  </React.Fragment>
);

GetToolComponents[ToolId.Gradle] = ({ userIsMember }) => {
  if (userIsMember) {
    return (
      <React.Fragment>
        <div>{Res.GetTool_Gradle_Preamble}</div>

        <div>
          <h2 className="title-xs">{Res.Step} 1</h2>
          <p>
            <FWLink linkId="2103880">{Res.InstallGradle}</FWLink>
          </p>
        </div>

        <div>
          <h2 className="title-xs">{Res.Step} 2</h2>
          <p>{Res.AddMavenSettings}</p>
          <CopyableCode>
            <pre>{`plugins {
  id 'net.linguica.maven-settings' version '0.5'
  id 'maven-publish'
}`}</pre>
          </CopyableCode>
        </div>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div>
          {Res.GetTool_Gradle_Preamble}
          <p>
            <FWLink linkId="2103880">{Res.InstallGradle}</FWLink>
          </p>
        </div>
      </React.Fragment>
    );
  }
};

GetToolComponents[ToolId.pip] = ({ userIsMember }) => {
  if (userIsMember) {
    return (
      <React.Fragment>
        <div>{Res.GetTool_Pip_Preamble}</div>

        <div>
          <h2 className="title-xs">{Res.Step} 1</h2>
          <p>
            <FWLink linkId="2103969">{Res.DownloadPython}</FWLink>
          </p>
        </div>

        <div>
          <h2 className="title-xs">{Res.Step} 2</h2>
          <p>{Res.UpdatePip}</p>
          <CopyableCode>python -m pip install --upgrade pip</CopyableCode>
        </div>

        <div>
          <h2 className="title-xs">{Res.Step} 3</h2>
          <p>{Res.InstallKeyring}</p>
          <CopyableCode>pip install keyring artifacts-keyring</CopyableCode>
        </div>

        <div>
          <h2 className="title-xs">{Res.Step} 4</h2>
          <p>
            <FormatComponent format={Res.InstallKeyring_Linux}>
              <FWLink underline linkId="2103695">
                {Res.Prerequisites}
              </FWLink>
              ,
            </FormatComponent>
          </p>
        </div>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <div>{Res.GetTool_PipOnly_Preamble}</div>

        <div>
          <h2 className="title-xs">{Res.Step} 1</h2>
          <p>
            <FWLink linkId="2103969">{Res.DownloadPython}</FWLink>
          </p>
        </div>

        <div>
          <h2 className="title-xs">{Res.Step} 2</h2>
          <p>{Res.UpdatePip}</p>
          <CopyableCode>python -m pip install --upgrade pip</CopyableCode>
        </div>
      </React.Fragment>
    );
  }
};

GetToolComponents[ToolId.twine] = () => (
  <React.Fragment>
    <div>{Res.GetTool_Twine_Preamble}</div>

    <div>
      <h2 className="title-xs">{Res.Step} 1</h2>
      <p>
        <FWLink linkId="2103969">{Res.DownloadPython}</FWLink>
      </p>
    </div>

    <div>
      <h2 className="title-xs">{Res.Step} 2</h2>
      <p>{Res.InstallTwine}</p>
      <CopyableCode>pip install twine keyring artifacts-keyring</CopyableCode>
    </div>

    <div>
      <h2 className="title-xs">{Res.Step} 3</h2>
      <p>
        <FormatComponent format={Res.InstallKeyring_Linux}>
          <FWLink underline linkId="2103695">
            {Res.Prerequisites}
          </FWLink>
          ,
        </FormatComponent>
      </p>
    </div>
  </React.Fragment>
);

GetToolComponents[ToolId.UniversalPackages] = () => (
  <React.Fragment>
    <div>{Res.GetTool_UPack_Preamble}</div>

    <div>
      <h2 className="title-xs">{Res.Step} 1</h2>
      <p>
        <FWLink linkId="2103879">{Res.InstallAzureCLI}</FWLink>
      </p>
    </div>

    <div>
      <h2 className="title-xs">{Res.Step} 2</h2>
      <p>{Res.InstallADOExtension}</p>
      <CopyableCode>az extension add --name azure-devops</CopyableCode>
    </div>
  </React.Fragment>
);

GetToolComponents[ToolId.Cargo] = () => (
  <React.Fragment>
    <div>{Res.GetTool_Cargo_Preamble}</div>

    <div>
      <h2 className="title-xs">{Res.Step} 1</h2>
      <p>
        <FWLink linkId="2207885">{Res.InstallRustup}</FWLink>
      </p>
    </div>
  </React.Fragment>
);

GetToolComponents[ToolId.Conda] = () => (
  <React.Fragment>
    <div>{Res.GetTool_Conda_Preamble}</div>

    <div>
      <h2 className="title-xs">{Res.Step} 1</h2>
      <p>
        <FWLink linkId="2234298">{Res.Conda_InstallDocs}</FWLink>
      </p>
      <h2 className="title-xs">{Res.Step} 2</h2>
      <p>
        <FormatComponent format={Res.Conda_UsesArtifactsCredProvider}>
          <code>{Res.Conda_UsesArtifactsCredProviderConda}</code>
          <code>{Res.Conda_UsesArtifactsCredProviderNuGet}</code>
        </FormatComponent>
      </p>
      <p className="list-disc">
        {Res.Conda_UsesArtifactsCredProviderPrereq}
        <ul>
          <li>{Res.Conda_UsesArtifactsCredProviderPrereq1}</li>
          <li>{Res.Conda_UsesArtifactsCredProviderPrereq2}</li>
          <li>{Res.Conda_UsesArtifactsCredProviderPrereq3}</li>
        </ul>
      </p>
      <p>{Res.Conda_UsesArtifactsCredProviderPrereqDetails}</p>
      <p>
        <FWLink linkId="2099625">{Res.InstallCredProvider}</FWLink>
      </p>
    </div>
  </React.Fragment>
);

GetToolComponents[ToolId.Ivy] = () => (
  <React.Fragment>
    <div>{Res.GetTool_Ivy_Preamble}</div>
  </React.Fragment>
);
